import React, { useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { Hidden, Spacer } from '../../helpers';
import { animations, colors, mixins } from '../../core/styles/theme/default';
import { Button, Link } from '../../primitives';
// @ts-ignore
import { Form } from 'react-stages';
import Textarea, { isValid as isValidTextarea } from './fields/Textarea';
import Checkbox, { isValid as isValidCheckbox } from './fields/Checkbox';
import Input, { isValid as isValidInput } from './fields/Input';
import Select, { isValid as isValidSelect } from './fields/Select';
import i18n from './digitID-i18n';
import FieldError from './fields/parts/FieldError';
import { EchIconArrowUpRight, EchIconDownload } from '../../primitives/IconSVG';
import { countriesArr, providerTypes } from './data/fieldsData';

const StyledForm = styled.form(
  ({ isSubmitting }: { isSubmitting: boolean }) => css`
    transition: opacity ${animations.default};

    ${isSubmitting &&
    css`
      pointer-events: none;
      opacity: 0.6;
    `}
  `,
);

const StyledFieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: none;
`;

const StyledLegend = styled.legend`
  color: ${colors.black};
  font-size: ${rem(18)};
  font-weight: 500;
  line-height: ${rem(30)};
`;

const StyledFieldsGroup = styled.div(
  ({ isCollection }: { isCollection?: boolean }) => css`
    position: relative;
    display: grid;
    grid-gap: ${rem(16)};

    ${mixins.responsiveStyles({
      'grid-template-columns': {
        zero: 'repeat(3, 1fr)',
        medium: 'repeat(6, 1fr)',
      },
    })}

    ${isCollection &&
    css`
      ${mixins.MQMax('medium')} {
        margin-right: ${rem(47)};
        padding-right: ${rem(15)};
        padding-top: ${rem(6)};
        border-right: ${rem(1)} solid ${colors.grue400};
      }

      ${StyledField}:last-of-type {
        ${mixins.MQ('medium')} {
          margin-right: ${rem(52)};
        }
      }
    `}
  `,
);

const StyledField = styled.div(
  ({
    fullWidth,
    span,
    hideLabel,
  }: {
    fullWidth?: boolean;
    span?: number;
    hideLabel?: boolean;
  }) => css`
    grid-column: span ${span ?? 3};

    ${fullWidth &&
    css`
      ${mixins.MQ('medium')} {
        grid-column: span 6;
      }
    `}

    ${hideLabel &&
    css`
      label {
        ${mixins.MQ('medium')} {
          display: none;
        }
      }
    `}

    input, textarea, select {
      background: ${colors.white};
      box-shadow: none;
      border: ${rem(1)} solid ${colors.grue400};
      border-radius: ${rem(2)};
      margin: 0;
      width: 100%;
      font-size: ${rem(16)};
      line-height: 1.5;
      color: ${colors.purple800};
      outline: none;
      transition: box-shadow ${animations.default};

      &:focus {
        outline: none;
        box-shadow: ${rem(-1)} ${rem(4)} ${rem(8)} ${rem(-2)}
          rgba(0, 0, 0, 0.15);
      }

      &[data-error='true'] {
        border-color: ${colors.error};
      }
    }
  `,
);

const StyledButton = styled(Button)`
  height: auto;
  min-height: ${rem(56)};
  padding-top: ${rem(12)};
  padding-bottom: ${rem(12)};

  ${mixins.responsiveStyles({
    'min-width': {
      zero: 288,
      medium: 308,
    },
  })}
`;

const StyledCollectionButton = styled.button(
  ({ variant }: { variant?: string }) => css`
    cursor: pointer;
    position: absolute;
    width: ${rem(34)};
    height: ${rem(34)};
    border: ${rem(2)} solid ${colors.purple800};
    background: ${colors.purple800};
    display: block;
    padding: 0;
    border-radius: 50%;
    transition: transform ${animations.default};

    ${mixins.responsiveStyles({
      right: {
        zero: -48,
        medium: 0,
      },
      top: {
        zero: `calc(50% - ${rem(17)})`,
        medium: 38,
      },
    })}

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${rem(18)};
      border-top: ${rem(2)} solid ${colors.white};
    }

    &:after {
      transform: translate(-50%, -50%) rotate(90deg);
    }

    &:hover,
    &:focus {
      position: absolute;
      transform: scale(1.2);
    }

    ${variant === 'remove' &&
    css`
      background: transparent;
      transform: rotate(45deg);

      ${mixins.MQ('medium')} {
        top: ${rem(12)};
      }

      &:hover,
      &:focus {
        transform: rotate(45deg) scale(1.2);
      }

      &:before,
      &:after {
        border-top-color: ${colors.purple800};
      }
    `}
  `,
);

const StyledSuccessMessage = styled.p`
  color: ${colors.black};
  font-weight: 400;
  margin: 0 0 ${rem(16)};

  ${mixins.responsiveStyles({
    'font-size': {
      zero: 20,
      medium: 24,
    },
    'line-height': {
      zero: 28,
      medium: 36,
    },
  })}
`;

const StyledErrorMessageWrapper = styled.div`
  ${mixins.responsiveStyles({
    'scroll-margin-top': { zero: 76, medium: 100 },
  })}
`;

const StyledErrorMessage = styled.p(
  ({ show }: { show: boolean }) => css`
    font-weight: 400;
    margin: 0 0 ${rem(16)};
    color: ${colors.error};

    ${!show &&
    css`
      display: none;
    `}
  `,
);

const StyledBackButton = styled(Link)`
  padding: 0;
`;

type FormDataType = { [key: string]: string | FormDataType[] };

const processErrorMessage = (error: any, locale: string) => {
  const propertyMap = {
    providerType: i18n.providerType[locale],
    chargerId: i18n.desiredID[locale],
    company: i18n.companyName[locale],
    uid: i18n.uid[locale],
    street: i18n.street[locale],
    zip: i18n.postcode[locale],
    city: i18n.location[locale],
    country: i18n.country[locale],
    url: i18n.website[locale],
    phone: i18n.phoneNumber[locale],
    email: i18n.email[locale],
    contactFirstName: i18n.firstName[locale],
    contactLastName: i18n.lastName[locale],
    contactRole: i18n.function[locale],
    contactPhone: i18n.phoneNumber[locale],
    contactEmail: i18n.email[locale],
  };

  return `Please correct the field <strong>${
    propertyMap[error.property as keyof typeof propertyMap]
  }</strong>: ${error.error.message ?? error.error.error.message}`;
};

const DigitIDRequestForm = ({
  locale,
  apiUrl,
  registerUrl,
  privacyPolicyUrl,
  termsAndConditionsUrl,
}: {
  locale: string;
  apiUrl: string;
  registerUrl: string;
  privacyPolicyUrl: string;
  termsAndConditionsUrl: string;
}) => {
  // With current version of react-stages package, we have to set initial values here instead of directly in the form fields
  const initialFormData: FormDataType = {
    country: i18n.switzerland[locale],
    providers: [{ providerType: '', chargerId: '' }],
  };
  const [data, setData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const errorMessageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = () => {
    setIsSubmitting(true);
    setErrorMessage(undefined);

    const formData = new FormData();

    // this mapping is needed because current version of react-stages does not support default values,
    // which results in chargerId not being included in the form data if user leaves it blank
    data.providers = (data.providers as FormDataType[]).map((item) => ({
      providerType: item.providerType,
      chargerId: item.chargerId ?? '',
    }));

    Object.keys(data).forEach((key) => {
      if (Array.isArray(data[key])) {
        (data[key] as FormDataType[]).forEach((item) => {
          Object.keys(item).forEach((subKey) => {
            formData.append(subKey, item[subKey] as string);
          });
        });
      } else {
        formData.append(key, data[key] as string);
      }
    });
    formData.append('locale', locale);

    fetch(apiUrl, {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          toggleContentVisibility(false);

          return null;
        } else {
          return response.json();
        }
      })
      .then((errorMsg) => {
        if (!errorMsg) return;

        setErrorMessage(processErrorMessage(errorMsg, locale));

        errorMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
      })
      .catch((err) => {
        console.log('Submit error: ', err);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const toggleContentVisibility = (isVisible: boolean) => {
    const content = document
      .getElementById('content')
      ?.querySelectorAll<HTMLElement>(':scope > *');

    if (content?.length) {
      if (isVisible) {
        content.forEach((element) => {
          element.style.display = '';
        });
      } else {
        const formWrapper =
          formRef.current?.closest<HTMLElement>('.m-richtext');

        formWrapper &&
          content.forEach((element) => {
            if (element !== formWrapper) {
              element.style.display = 'none';
            }
          });
      }
    }

    setData(initialFormData);
    setIsSubmitted(!isVisible);
  };

  return (
    <Form
      id={`digitIDRequestForm-${locale}`}
      data={data}
      fields={{
        text: {
          component: Input,
          isValid: isValidInput,
        },
        email: {
          component: Input,
          isValid: isValidInput,
        },
        tel: {
          component: Input,
          isValid: isValidInput,
        },
        checkbox: {
          component: Checkbox,
          isValid: isValidCheckbox,
        },
        textarea: {
          component: Textarea,
          isValid: isValidTextarea,
        },
        select: {
          component: Select,
          isValid: isValidSelect,
        },
      }}
      onChange={(payload: FormDataType) => setData(payload)}
      config={{
        fields: () => [
          {
            id: 'providers',
            type: 'collection',
            init: true,
            fields: [
              {
                id: 'providerType',
                type: 'select',
                label: i18n.providerType[locale],
                locale,
                isRequired: true,
                options: [
                  { value: '', label: '' },
                  ...Object.keys(providerTypes(locale)).map((key) => ({
                    value: key,
                    label: providerTypes(locale)[key],
                  })),
                ],
              },
              {
                id: 'chargerId',
                type: 'text',
                label: i18n.desiredID[locale],
                locale,
                tooltip: i18n.desiredIDTooltip[locale].replace(
                  '{LINK}',
                  registerUrl,
                ),
              },
            ],
          },
          {
            id: 'company',
            label: i18n.companyName[locale],
            type: 'text',
            locale,
            isRequired: true,
          },
          {
            id: 'uid',
            label: i18n.uid[locale],
            type: 'text',
            locale,
            tooltip: i18n.uidTooltip[locale],
            isRequired: true,
          },
          {
            id: 'street',
            label: i18n.street[locale],
            type: 'text',
            locale,
            isRequired: true,
          },
          {
            id: 'zip',
            label: i18n.postcode[locale],
            type: 'text',
            locale,
            isRequired: true,
            customValidation: ({ data, fieldConfig }: any) => {
              if (!data && fieldConfig.isRequired) {
                return false;
              }

              if (data && !data.match(/^[A-Z0-9 -]{2,10}$/)) {
                return 'INVALID_POSTCODE';
              }

              return true;
            },
            errorRenderer: (error: any) =>
              FieldError({
                text:
                  error.errorCode === 'INVALID_POSTCODE'
                    ? i18n.invalidPostcode[locale]
                    : i18n.requiredField[locale],
              }),
          },
          {
            id: 'city',
            label: i18n.location[locale],
            type: 'text',
            locale,
            isRequired: true,
          },
          {
            id: 'country',
            label: i18n.country[locale],
            type: 'select',
            locale,
            isRequired: true,
            options: countriesArr(locale).map((country) => ({
              value: country,
              label: country,
            })),
          },
          {
            id: 'url',
            label: i18n.website[locale],
            type: 'text',
            locale,
            isRequired: true,
            customValidation: ({ data, fieldConfig }: any) => {
              if (!data && fieldConfig.isRequired) {
                return false;
              }

              if (
                data &&
                !data.match(
                  /^(http:\/\/|https:\/\/|www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\/?$/,
                )
              ) {
                return 'INVALID_URL';
              }

              return true;
            },
            errorRenderer: (error: any) =>
              FieldError({
                text:
                  error.errorCode === 'INVALID_URL'
                    ? i18n.invalidUrl[locale]
                    : i18n.requiredField[locale],
              }),
          },
          {
            id: 'phone',
            label: i18n.phoneNumber[locale],
            type: 'tel',
            locale,
            isRequired: true,
            customValidation: ({ data, fieldConfig }: any) => {
              if (!data && fieldConfig.isRequired) {
                return false;
              }

              if (data && !data.match(/^[+]?[0-9\s-]{7,15}$/)) {
                return 'INVALID_NUMBER';
              }

              return true;
            },
            errorRenderer: (error: any) =>
              FieldError({
                text:
                  error.errorCode === 'INVALID_NUMBER'
                    ? i18n.invalidPhone[locale]
                    : i18n.requiredField[locale],
              }),
          },
          {
            id: 'email',
            label: i18n.email[locale],
            type: 'email',
            locale,
            isRequired: true,
            customValidation: ({ data, fieldConfig }: any) => {
              if (!data && fieldConfig.isRequired) {
                return false;
              }

              if (
                data &&
                !data.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
              ) {
                return 'INVALID_EMAIL';
              }

              return true;
            },
            errorRenderer: (error: any) =>
              FieldError({
                text:
                  error.errorCode === 'INVALID_EMAIL'
                    ? i18n.invalidEmail[locale]
                    : i18n.requiredField[locale],
              }),
          },
          {
            id: 'contactFirstName',
            label: i18n.firstName[locale],
            type: 'text',
            locale,
            isRequired: true,
          },
          {
            id: 'contactLastName',
            label: i18n.lastName[locale],
            type: 'text',
            locale,
            isRequired: true,
          },
          {
            id: 'contactRole',
            label: i18n.function[locale],
            type: 'text',
            locale,
            isRequired: true,
          },
          {
            id: 'contactPhone',
            label: i18n.phoneNumber[locale],
            type: 'tel',
            locale,
            isRequired: true,
            customValidation: ({ data, fieldConfig }: any) => {
              if (!data && fieldConfig.isRequired) {
                return false;
              }

              if (data && !data.match(/^[+]?[0-9\s-]{7,15}$/)) {
                return 'INVALID_NUMBER';
              }

              return true;
            },
            errorRenderer: (error: any) =>
              FieldError({
                text:
                  error.errorCode === 'INVALID_NUMBER'
                    ? i18n.invalidPhone[locale]
                    : i18n.requiredField[locale],
              }),
          },
          {
            id: 'contactEmail',
            label: i18n.email[locale],
            type: 'email',
            locale,
            isRequired: true,
            customValidation: ({ data, fieldConfig }: any) => {
              if (!data && fieldConfig.isRequired) {
                return false;
              }

              if (
                data &&
                !data.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
              ) {
                return 'INVALID_EMAIL';
              }

              return true;
            },
            errorRenderer: (error: any) =>
              FieldError({
                text:
                  error.errorCode === 'INVALID_EMAIL'
                    ? i18n.invalidEmail[locale]
                    : i18n.requiredField[locale],
              }),
          },
          {
            id: 'termsAcceptance',
            label: i18n.termsAcceptance[locale]
              .replace('{FILE_LINK}', termsAndConditionsUrl)
              .replace(
                '{DOWNLOAD_ICON}',
                renderToString(<EchIconDownload width="20px" height="20px" />),
              )
              .replace('{LINK}', privacyPolicyUrl)
              .replace(
                '{EXTERNAL_ICON}',
                renderToString(
                  <EchIconArrowUpRight width="22px" height="22px" />,
                ),
              ),
            type: 'checkbox',
            locale,
            isRequired: true,
            errorRenderer: () =>
              FieldError({
                text: i18n.acceptTermsError[locale],
              }),
          },
        ],
      }}
      render={({ actionProps, fieldProps }: any) => (
        <>
          {!isSubmitted ? (
            <>
              <StyledErrorMessageWrapper ref={errorMessageRef}>
                <StyledErrorMessage
                  show={!!errorMessage}
                  dangerouslySetInnerHTML={{ __html: errorMessage ?? '' }}
                />
              </StyledErrorMessageWrapper>
              <StyledForm
                ref={formRef}
                onSubmit={(e: React.FormEvent<HTMLFormElement>) =>
                  e.preventDefault()
                }
                noValidate
                isSubmitting={isSubmitting}
              >
                <StyledFieldset>
                  {fieldProps.fields.providers?.map(
                    (subFields: any, index: number) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <>
                            <Hidden below="medium">
                              <Spacer space="delta" />
                            </Hidden>
                            <Hidden above="medium">
                              <Spacer space="epsilon" />
                            </Hidden>
                          </>
                        )}
                        <StyledFieldsGroup isCollection>
                          <StyledField hideLabel={index > 0}>
                            {subFields.providerType}
                          </StyledField>
                          <StyledField hideLabel={index > 0}>
                            {subFields.chargerId}
                          </StyledField>
                          {index === 0 &&
                          fieldProps.fields.providers?.length < 10 ? (
                            <StyledCollectionButton
                              type="button"
                              aria-label={i18n.addLine[locale]}
                              onClick={() => {
                                fieldProps.onCollectionAction(
                                  'providers',
                                  'add',
                                );
                              }}
                            />
                          ) : null}
                          {index > 0 ? (
                            <StyledCollectionButton
                              type="button"
                              aria-label={i18n.removeLine[locale]}
                              variant="remove"
                              onClick={() => {
                                fieldProps.onCollectionAction(
                                  'providers',
                                  'remove',
                                  index,
                                );
                              }}
                            />
                          ) : null}
                        </StyledFieldsGroup>
                      </React.Fragment>
                    ),
                  )}
                </StyledFieldset>
                <Spacer space="delta" />
                <StyledFieldset>
                  <StyledLegend>{i18n.company[locale]}</StyledLegend>
                  <Spacer space="gamma" />
                  <StyledFieldsGroup>
                    <StyledField>{fieldProps.fields.company}</StyledField>
                    <StyledField>{fieldProps.fields.uid}</StyledField>
                    <StyledField>{fieldProps.fields.street}</StyledField>
                    <StyledField span={1}>{fieldProps.fields.zip}</StyledField>
                    <StyledField span={2}>{fieldProps.fields.city}</StyledField>
                    <StyledField>{fieldProps.fields.country}</StyledField>
                    <StyledField>{fieldProps.fields.url}</StyledField>
                    <StyledField>{fieldProps.fields.phone}</StyledField>
                    <StyledField>{fieldProps.fields.email}</StyledField>
                  </StyledFieldsGroup>
                </StyledFieldset>
                <Spacer space="delta" />
                <StyledFieldset>
                  <StyledLegend>{i18n.contact[locale]}</StyledLegend>
                  <Spacer space="gamma" />
                  <StyledFieldsGroup>
                    <StyledField>
                      {fieldProps.fields.contactFirstName}
                    </StyledField>
                    <StyledField>
                      {fieldProps.fields.contactLastName}
                    </StyledField>
                    <StyledField fullWidth>
                      {fieldProps.fields.contactRole}
                    </StyledField>
                    <StyledField>{fieldProps.fields.contactPhone}</StyledField>
                    <StyledField>{fieldProps.fields.contactEmail}</StyledField>
                  </StyledFieldsGroup>
                </StyledFieldset>
                <Spacer space="epsilon" />
                <StyledField>{fieldProps.fields.termsAcceptance}</StyledField>
                <Spacer space="epsilon" />
                <StyledButton
                  buttonText={i18n.submitButton[locale]}
                  type="submit"
                  onClick={() => actionProps.handleActionClick(onSubmit, true)}
                />
              </StyledForm>
            </>
          ) : (
            <>
              <StyledSuccessMessage
                dangerouslySetInnerHTML={{
                  __html: i18n.successMessage[locale],
                }}
              />
              <StyledBackButton
                linkText={i18n.backToForm[locale]}
                as="button"
                iconBefore="EchIconLeft"
                color={colors.purple800}
                onClick={() => toggleContentVisibility(true)}
              />
            </>
          )}
        </>
      )}
    />
  );
};

export default DigitIDRequestForm;
